<template>
  <v-menu
    ref="menu"
    v-model="menu"
    :close-on-content-click="false"
    :offset-x="offsetX"
    :offset-y="offsetY"
    min-width="auto">
    <template v-slot:activator="{ on, attrs }">
      <div class="d-flex align-center">
        <v-text-field
          v-model="textFieldValue"
          :filled="filled"
          :label="label"
          :prepend-inner-icon="prependInnerIcon"
          :error="error"
          :error-messages="errorMessages"
          :hide-details="hideDetails"
          @keyup.enter="textFieldUpdated"
          @blur="textFieldUpdated"/>
        <vf-tooltip :text="$t('open_date_picker')">
          <v-btn fab small text v-bind="attrs" v-on="on" class="mb-1" tabindex="-1">
            <v-icon color="primary">mdi-calendar-edit</v-icon>
          </v-btn>
        </vf-tooltip>
      </div>
    </template>
    <v-date-picker v-model="internalValue" :type="type" no-title scrollable @input="menu = false">
      <v-btn v-if="today" text color="primary" @click="setToToday()">
        {{ $t('today') }}
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn text small fab color="primary" @click="clear()">
        <v-icon>mdi-backspace</v-icon>
      </v-btn>
      <v-btn text small color="primary" fab @click="menu = false">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-date-picker>
  </v-menu>
</template>

<script>
import DatePicker from '@/components/shared/DatePicker'
import VfTooltip from '@/components/shared/VfTooltip'
import Moment from 'moment-timezone'

/**
 * Extended version of the DatePicker that lets you type in dates. This is not the default method of operation because
 * it deviates a bit from the normal pattern, but we need it for the Route Create form so people can just type in dates
 * instead of dealing with the date picker.
 */
export default {
  extends: DatePicker,
  name: 'DatePickerTypeIn',
  components: {
    VfTooltip
  },
  props: {
    hideDetails: {
      type: Boolean,
      default: false
    },
    offsetX: {
      type: Boolean,
      default: false
    },
    offsetY: {
      type: Boolean,
      default: true
    },
    format: {
      type: String,
      default: null
    },
    noTitle: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      // The value we store and use in the text field. This has to be a separate piece of state, so we can hold off
      // updating it until a blur or submit happens.
      textFieldValue: null
    }
  },
  methods: {
    /**
     * Clear the component's values.
     */
    clear () {
      this.internalValue = null
      this.textFieldValue = null
      this.menu = false
    },
    /**
     * When the text field is blurred or the user hits enter, update the internal value from the text field.
     * This uses the JS Date constructor, so behavior will not be universal among browsers, but it should be okay
     * as users will get used to the particular behavior of their browser (I hope, at least.)
     * This should be locale aware on the browser end, so non-US users will be able to type in DD/MM/YY or some-such
     */
    textFieldUpdated () {
      if (this.textFieldValue) {
        const parsedDate = new Date(this.textFieldValue)
        // Funky way of determining whether the parsed date is invalid or not. I couldn't find a better way to do this.
        if (!Number.isNaN(parsedDate.getTime())) {
          // Parse it back into the ISO-8601 date format we use for the internal representation
          this.internalValue = Moment(parsedDate).format('YYYY-MM-DD')
        } else {
          this.internalValue = null
        }
      }
    },
    /**
     * When the internal value changes, change it to use the usual locale-aware date format via Moment's 'L'
     * @param newValue
     */
    updateTextField (newValue) {
      if (newValue) {
        this.textFieldValue = Moment(newValue).format('L')
      } else {
        this.textFieldValue = null
      }
    }
  },
  watch: {
    /**
     * When the internal value changes, change it to use the usual locale-aware date format via Moment's 'L'
     * @param newValue
     */
    internalValue (newValue) {
      this.updateTextField(newValue)
    }
  },
  created () {
    this.updateTextField(this.value)
  }
}
</script>

<style scoped>

</style>
